/**
 * Copyright (c) 2026 Finorda Pty Ltd
 * All rights reserved.
 *
 * This file is part of the Finorda platform.
 * Unauthorised copying, modification or
 * distribution of this software is
 * strictly prohibited.
 *
 * Proprietary and confidential.
 * Governed by Queensland law, Australia.
 */

import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { poService, supplierService } from '../../services/api'
import POTableRow from '../../components/POTableRow'
import StatusCards from './StatusCards'
import FilterBar from './FilterBar'

/**
 * PurchaseOrders - POG List Page
 * Status workflow matches QCS pattern: DRAFT -> ISSUED -> ARCHIVED
 * Action buttons: View, Issue (draft), PDF (issued/archived), Archive (non-archived)
 */
function PurchaseOrders() {
  const navigate = useNavigate()
  const location = useLocation()
  const [purchaseOrders, setPurchaseOrders] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [showArchived, setShowArchived] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [supplierFilter, setSupplierFilter] = useState('')
  const [supplierMapping, setSupplierMapping] = useState([])
  const [message, setMessage] = useState(location.state?.message || '')
  const [actionLoading, setActionLoading] = useState(null)

  useEffect(() => { loadData() }, [supplierFilter])
  useEffect(() => {
    if (message) { const t = setTimeout(() => setMessage(''), 5000); return () => clearTimeout(t) }
  }, [message])

  const loadData = async () => {
    setLoading(true)
    try {
      const [posRes, mappingRes] = await Promise.all([
        poService.getAll({ supplierNumber: supplierFilter || undefined }),
        supplierService.getMapping()
      ])
      setPurchaseOrders(posRes.purchaseOrders || [])
      setTotal(posRes.total || 0)
      setSupplierMapping(mappingRes.mappings || [])
    } catch (error) { console.error('Failed to load POs:', error) }
    finally { setLoading(false) }
  }

  // Status action helper (issue / archive)
  const handleStatusAction = async (e, poId, status, confirmMsg, successMsg) => {
    e.stopPropagation()
    if (!window.confirm(confirmMsg)) return
    setActionLoading(poId)
    try {
      await poService.updateStatus(poId, status)
      await loadData()
      setMessage(successMsg)
    } catch (error) { alert(`Failed: ${error.message || 'Unknown error'}`) }
    finally { setActionLoading(null) }
  }
  const handleIssue = (e, poId) => handleStatusAction(e, poId, 'ISSUED', 'Issue this PO? This will lock it and generate a PDF.', 'PO issued successfully')
  const handleArchive = (e, poId) => handleStatusAction(e, poId, 'ARCHIVED', 'Are you sure you want to archive this PO?', 'PO archived')

  const handlePDF = (e, poId, poNumber) => {
    e.stopPropagation()
    const link = Object.assign(document.createElement('a'), { href: poService.getPdfUrl(poId), download: poNumber + '.pdf', target: '_blank' })
    link.click()
  }

  // Filter POs - exclude archived unless showArchived checked
  const filteredPOs = purchaseOrders.filter(po => {
    const status = po.status?.toUpperCase() || 'DRAFT'
    if (!showArchived && status === 'ARCHIVED') return false
    if (!searchTerm) return true
    const sl = searchTerm.toLowerCase()
    return po.po_number?.toLowerCase().includes(sl) || po.job_number?.toLowerCase().includes(sl) ||
      po.job_name?.toLowerCase().includes(sl) || po.supplier_name_snapshot?.toLowerCase().includes(sl)
  })

  // Calculate stats for status cards
  const stats = {
    total: purchaseOrders.filter(po => (po.status?.toUpperCase() || 'DRAFT') !== 'ARCHIVED').length,
    draft: purchaseOrders.filter(po => !po.status || po.status.toUpperCase() === 'DRAFT').length,
    issued: purchaseOrders.filter(po => po.status?.toUpperCase() === 'ISSUED').length,
    archived: purchaseOrders.filter(po => po.status?.toUpperCase() === 'ARCHIVED').length
  }

  const secBtnStyle = {
    padding: '10px 20px', fontSize: '14px', fontWeight: '600',
    backgroundColor: '#f3f4f6', color: '#374151',
    border: '1px solid #d1d5db', borderRadius: '6px', textDecoration: 'none'
  }
  const priBtnStyle = {
    padding: '10px 20px', fontSize: '14px', fontWeight: '600',
    backgroundColor: '#1a5276', color: 'white',
    border: 'none', borderRadius: '6px', textDecoration: 'none'
  }
  const thBase = { padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }
  const columns = [
    { label: 'PO Number' }, { label: 'Job' }, { label: 'Supplier' },
    { label: 'Total', align: 'right' }, { label: 'Status', align: 'center' },
    { label: 'Date' }, { label: 'Actions', extra: { minWidth: '220px' } }
  ]

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }} data-testid="purchase-orders-page">
      {/* Header with action buttons */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', margin: 0 }} data-testid="page-heading">Purchase Orders</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <Link to="/window-po" data-testid="btn-window-door-po" style={secBtnStyle}>Window/Door PO</Link>
          <Link to="/manual-po" data-testid="btn-manual-po" style={secBtnStyle}>Manual PO</Link>
          <Link to="/csv-upload" data-testid="btn-upload-csv" style={priBtnStyle}>+ Upload CSV</Link>
        </div>
      </div>

      {/* Success message */}
      {message && (
        <div style={{
          backgroundColor: '#d1fae5', border: '1px solid #6ee7b7',
          borderRadius: '8px', padding: '12px 16px', marginBottom: '20px', color: '#065f46'
        }}>{message}</div>
      )}

      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        supplierFilter={supplierFilter}
        onSupplierFilterChange={setSupplierFilter}
        supplierMapping={supplierMapping}
        showArchived={showArchived}
        onShowArchivedChange={setShowArchived}
      />

      <StatusCards stats={stats} />

      {/* PO Table */}
      <div style={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px' }}>
            <div style={{ fontSize: '16px', color: '#6b7280' }}>Loading purchase orders...</div>
          </div>
        ) : filteredPOs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
            <p style={{ marginBottom: '20px' }}>{searchTerm ? 'No POs match your search.' : 'No purchase orders yet.'}</p>
            <Link to="/csv-upload" style={{ ...priBtnStyle, display: 'inline-block', padding: '12px 24px' }}>
              Upload CSV to Create POs
            </Link>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }} data-testid="po-table">
            <thead style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              <tr>
                {columns.map(col => (
                  <th key={col.label} style={{ ...thBase, ...(col.align ? { textAlign: col.align } : {}), ...(col.extra || {}) }}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredPOs.map(po => (
                <POTableRow key={po.id} po={po} actionLoading={actionLoading}
                  onIssue={handleIssue} onArchive={handleArchive}
                  onPDF={handlePDF} onNavigate={navigate} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

export default PurchaseOrders
